import type { CSSProperties } from 'react'

import { CURRENT_CLIENT } from '@/constants'
import { buildHorizontalScrollEdgeMaskStyle } from '@/lib/horizontal-scroll-edge-mask'
import { resolveComposerConfig } from '@/lib/resolve-composer-config'
import type { ComposerSuggestionPillStyle } from '@/types/composer'
import { cn } from '@/lib/utils'

/** Max reading width for user bubbles and document-mode prose band — not in-chat composer. */
export const CHAT_MESSAGE_COLUMN = 'w-full max-w-[min(85%,32rem)]'

/** Document-mode row: right-align the shared band (user bubble + assistant column). */
export const CHAT_DOCUMENT_ROW = 'flex w-full min-w-0 flex-col items-end gap-1.5'

/** Horizontal + top padding for the in-chat scroll content; bottom inset is dynamic (composer height). */
export function getChatThreadScrollPaddingClassName(): string {
  return 'box-border px-4 pt-4'
}

/**
 * Floating composer overlay over the full-height thread scroll area. Edge fades
 * are backdrop-blur strips; a composer plate hides content behind the dock.
 * `pointer-events-none` keeps the strip above the composer click-through; the
 * inner band restores pointer events.
 */
export function getComposerOverlayClassName(): string {
  return 'pointer-events-none absolute inset-x-0 bottom-0 z-10'
}

/** Equidistant horizontal + bottom inset for the in-chat composer dock (matches thread `px-4`). */
export function getComposerDockBandClassName(): string {
  return 'pointer-events-auto px-4 pb-4'
}

/**
 * Document-mode uses the full message row because the user bubble spans the row
 * in the actual chat pane; the text inset still matches bubble padding.
 */
export const CHAT_DOCUMENT_SHELL = 'w-full box-border px-3 pb-1'
export const CHAT_DOCUMENT_SUGGESTIONS_SHELL = 'w-full box-border px-3'
export const CHAT_DOCUMENT_USER_BUBBLE = 'w-full box-border p-3 bg-muted/50 rounded-lg'

/** Shared Tailwind Typography classes for chat markdown bodies. */
export const CHAT_MESSAGE_PROSE =
  'prose prose-sm dark:prose-invert w-full !max-w-none text-sm prose-headings:mt-0 prose-headings:mb-2 prose-headings:text-base prose-p:mb-4 prose-p:mt-0 prose-p:text-sm prose-p:leading-relaxed prose-ul:my-2 prose-ul:list-disc prose-ul:pl-6 prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-0 prose-li:text-sm prose-code:text-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-muted prose-pre:p-3 prose-pre:rounded prose-pre:overflow-x-auto prose-a:text-primary prose-a:underline hover:prose-a:text-primary/80 prose-strong:font-semibold prose-blockquote:border-l-4 prose-blockquote:border-muted-foreground prose-blockquote:pl-4 prose-blockquote:italic prose-table:my-2 prose-th:border prose-th:border-border prose-td:border prose-td:border-border'

export const CHAT_USER_BUBBLE = `bg-muted/50 rounded-lg p-3 ${CHAT_MESSAGE_COLUMN}`

export const CHAT_ASSISTANT_BUBBLE = 'bg-muted/50 rounded-lg p-3'

type ClientChatLayout = {
  chatMessageStyle?: 'classic' | 'document'
  chatUserAvatar?: 'icon' | 'profile'
}

function getClientChatLayout(): ClientChatLayout {
  return (CURRENT_CLIENT as { layout?: ClientChatLayout }).layout ?? {}
}

export function getChatMessageStyle(): 'classic' | 'document' {
  return getClientChatLayout().chatMessageStyle ?? 'classic'
}

export function getChatUserAvatarStyle(): 'icon' | 'profile' {
  return getClientChatLayout().chatUserAvatar ?? 'icon'
}

export function isDocumentChatStyle(): boolean {
  return getChatMessageStyle() === 'document'
}

const SUGGESTION_PILL_BASE =
  'inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

const SUGGESTION_PILL_FILLED = cn(
  SUGGESTION_PILL_BASE,
  'bg-muted hover:bg-muted/80 hover:text-foreground'
)

const SUGGESTION_PILL_OUTLINE = cn(
  SUGGESTION_PILL_BASE,
  'border border-border bg-background hover:bg-accent hover:text-accent-foreground'
)

export function getSuggestionPillClassName(
  style: ComposerSuggestionPillStyle = 'outline'
): string {
  return style === 'filled' ? SUGGESTION_PILL_FILLED : SUGGESTION_PILL_OUTLINE
}

/** Tenant config is static at runtime — resolve once and reuse. */
let resolvedSuggestionPillClassName: string | null = null

export function getResolvedSuggestionPillClassName(): string {
  if (resolvedSuggestionPillClassName === null) {
    const { suggestionPillStyle } = resolveComposerConfig(
      CURRENT_CLIENT as Parameters<typeof resolveComposerConfig>[0]
    )
    resolvedSuggestionPillClassName = getSuggestionPillClassName(suggestionPillStyle)
  }
  return resolvedSuggestionPillClassName
}

const CAPABILITY_MENU_PILL_BASE =
  'inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs transition-[color,background-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50'

export function getCapabilityMenuTrayClassName(isInChat: boolean): string {
  return cn(
    isInChat
      ? 'flex w-max min-w-full items-center gap-1.5'
      : 'flex w-full min-w-0 flex-wrap items-center justify-center gap-1.5',
  )
}

/** Horizontal scroll for in-chat capability pills above the composer. */
export function getCapabilityMenuInChatScrollClassName(): string {
  return 'no-scrollbar overflow-x-auto px-[3px] py-1'
}

/**
 * Overlay shell for the in-chat capability strip: floats above the composer
 * (absolute, out of flow) so showing/hiding it never resizes the footer,
 * shifts the conversation, or changes the message-list scroll padding.
 */
export function getInChatCapabilityStripOverlayClassName(visible: boolean): string {
  return cn(
    'absolute bottom-full left-0 right-0 z-20',
    'transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none',
    visible
      ? 'pointer-events-auto translate-y-0 opacity-100'
      : 'pointer-events-none translate-y-1 opacity-0',
  )
}

/**
 * Horizontal overflow fades for the in-chat capability strip: mask on the
 * scroll container so pills dissolve at the edges instead of painting a
 * gradient over decorative page backgrounds. Same technique as the thread
 * fade in useChatThreadScrollEdges.
 */
export function getCapabilityMenuTrayMaskStyle(
  showLeftFade: boolean,
  showRightFade: boolean,
): CSSProperties | undefined {
  return buildHorizontalScrollEdgeMaskStyle(showLeftFade, showRightFade)
}

export function getCapabilityMenuPillClassName(options?: { isOpen?: boolean }): string {
  const { isOpen = false } = options ?? {}
  return cn(
    CAPABILITY_MENU_PILL_BASE,
    isOpen
      ? 'bg-primary/5 text-foreground shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.4)]'
      : 'text-muted-foreground shadow-[inset_0_0_0_1px_transparent] hover:bg-accent hover:text-accent-foreground',
  )
}

/** Example row label in the capability drawer — matches pill tray scale (text-xs). */
export function getCapabilityDrawerExampleLabelClassName(): string {
  return 'text-xs text-popover-foreground'
}

/** Secondary line under an example on mobile drawers. */
export function getCapabilityDrawerExampleHintClassName(): string {
  return 'line-clamp-2 text-[11px] leading-snug text-muted-foreground'
}

/** In-chat composer outer band — full chat column width; parent supplies horizontal px. */
export function getComposerBandOuterClassName(options: {
  centered: boolean
  alignWithDocumentBand: boolean
}): string {
  const { centered, alignWithDocumentBand } = options
  return cn(
    'min-w-0 shrink-0',
    centered ? 'w-full' : alignWithDocumentBand ? CHAT_DOCUMENT_ROW : 'w-full min-w-0',
  )
}

/** In-chat composer inner shell width — classic uses full band; hero centered uses max-w-3xl. */
export function getComposerBandInnerClassName(options: {
  centered: boolean
}): string {
  const { centered } = options
  return cn('w-full min-w-0', centered && 'mx-auto max-w-3xl')
}
